import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { SuggestionDataMap } from 'components/WorkspaceEditor/SuggestionStore'
import {
    ContentItemViewNodesType,
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from 'components/WorkspaceEditor/types'
import {
    TrackChangeStatus,
    TrackChangeType,
    TrackedChange,
} from 'models/changes'
import { KEY_ID_SUFFIX, TRACK_CHANGE_STATUS_VALUES } from '../constants'
import { UpdatedTrackChangesData } from '../hooks/useHandleCardActions'
import { Action } from '../types/types'

/**
 * Flattens nested content elements into a mapping.
 * The key includes the parent content ID if requested.
 *
 * @param {Partial<ContentItemViewNodesType>[]} contentItemViewNode - The content item view nodes to process.
 * @param {boolean} [shouldAddParentContentId=false] - Whether to include the parent content ID in the mapping keys.
 * @returns {Record<string, Partial<TextContentElement>>} - A mapping of content item view node IDs to text content elements.
 */
export const getContentItemViewNodeElementMapping = (
    contentItemViewNode: Partial<ContentItemViewNodesType>[],
    shouldAddParentContentId: boolean = false
): Record<string, Partial<TextContentElement>> => {
    const mapping: Record<string, Partial<TextContentElement>> = {}
    for (const item of contentItemViewNode) {
        if (item?.elements) {
            for (const element of item.elements) {
                const baseKey = `${item.id}_${element.id}`
                mapping[baseKey] = element
                // Recursively process nested elements
                processNestedElements(
                    mapping,
                    item.id,
                    element,
                    shouldAddParentContentId ? item.id : undefined
                )
            }
        }
    }
    return mapping
}

/**
 * Recursively adds nested elements into the flat mapping.
 *
 * @param {Record<string, Partial<TextContentElement>>} acc - The accumulator for the mapping.
 * @param {string | undefined} parentId - The ID of the parent content item.
 * @param {Partial<TextContentElement>} element - The current text content element to process.
 * @param {string | undefined} [parentContentId=undefined] - The ID of the parent content item to include in the mapping keys.
 */
const processNestedElements = (
    acc: Record<string, Partial<TextContentElement>>,
    parentId: string | undefined,
    element: Partial<TextContentElement>,
    parentContentId: string | undefined = undefined
): void => {
    if (!Array.isArray(element.elements)) return // early return
    for (const nested of element.elements) {
        const key = `${parentContentId ? `${parentContentId}_` : ''}${
            element.id
        }_${nested.id}`
        acc[key] = nested
        // Recursively process deeper levels of nested elements
        processNestedElements(acc, parentId, nested, parentContentId)
    }
}

/**
 * Retrieves track changes data for a specific suggestion ID.
 *
 * @param {string} suggestionId - The ID of the suggestion to retrieve track changes data for.
 * @param {Record<string, TrackedChange>} trackChangesData - The track changes data to search.
 * @returns {TrackedChange | undefined} - The track changes data for the specified suggestion ID, or undefined if not found.
 */
export const getTrackChangesDataById = (
    suggestionId: string,
    trackChangesData: Record<string, TrackedChange>
): TrackedChange | undefined => {
    return Object.values(trackChangesData).find((item) =>
        item.contentItemElementId.includes(suggestionId)
    )
}

/**
 /**
 * This function identifies elements without siblings in the contentItemViewNode.
 * 
 * Example: 
 * Suppose element A is modified. It may split into:
 *   - Element 1: content before modification
 *   - Element B: modified content
 *   - Element C: content after modification
 * 
 * If the result is a single element with no siblings, it indicates a full addition or deletion.
 * Otherwise, it means the element was partially updated.
 * @param {Partial<ContentItemViewNodesType>[]} contentItemViewNodes - The content item view nodes to process.
 * @param {string[]} ids - The IDs of the elements to process.
 * @param {Action} action - The action to perform (accept, discard, etc.).
 * @param {(elementId: string, text: string, contentItemViewNodes: Partial<ContentItemViewNodesType>[]) => Partial<ContentItemViewNodesType>[]} updateText - A function to update the text of an element.
 * @returns {{ updatedContentItem: Partial<ContentItemViewNodesType>[]; nonSiblingElements: Record<string, Partial<TextContentElement>> }} - The updated content item view nodes and the non-sibling elements.
 */
export const processNonSiblingElementsForTrackChanges = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    ids: string[]
): Record<string, Partial<TextContentElement>> => {
    const elementMapping = getContentItemViewNodeElementMapping(
        contentItemViewNodes
    )
    const elements = Object.keys(elementMapping).filter((key) =>
        ids.includes(key.split('_')[1])
    )

    const nonSiblingElements: Record<string, Partial<TextContentElement>> = {}

    elements.forEach((element) => {
        const [parentId, elementId] = element.split('_')
        const siblingKeys = Object.keys(elementMapping).filter(
            (key) => key.includes(parentId) && !key.includes(elementId)
        )
        if (!siblingKeys.length) {
            nonSiblingElements[elementId] = elementMapping[element]
        }
    })

    return nonSiblingElements
}

/**
 * Updates the text content of a node in the content item view nodes.
 * @param nodeId - The ID of the node to update.
 * @param nodes - The array of content item view nodes.
 * @returns The updated array of content item view nodes.
 */
const ModifyTextElementById = (
    nodeId: string = '',
    nodes: Partial<ContentItemViewNodesType>[]
): Partial<ContentItemViewNodesType>[] => {
    //here find the node by id and made change type null
    return nodes.map((node) => {
        if (node.id === nodeId) {
            return {
                ...node,
                authoredUserId: null,
                changeType: null,
            }
        } else if (node.elements) {
            return {
                ...node,
                elements: ModifyTextElementById(nodeId, node.elements),
            }
        }
        return node
    })
}

/**
 * Utility function to group elements by their parent ID.
 * This reduces the number of iterations needed to find siblings.
 * @param elementMapping 
 * @returns Map<string, {
    key: string;
    element: Partial<TextContentElement>;
}[]>
 */
export const groupElementsByParent = (
    elementMapping: Record<string, Partial<TextContentElement>>
) => {
    const parentGroups = new Map<
        string,
        Array<{ key: string; element: Partial<TextContentElement> }>
    >()
    for (const [key, element] of Object.entries(elementMapping)) {
        const parentId = key.split('_')[0]
        if (!parentGroups.has(parentId)) {
            parentGroups.set(parentId, [])
        }
        parentGroups.get(parentId)!.push({ key, element })
    }
    return parentGroups
}

/**
 * Utility function to map suggestion IDs to their corresponding keys in the element mapping.
 * This reduces the number of lookups needed when processing suggestions.
 * @param elementMapping
 * @param suggestionsDataMap
 * @returns Map<string, string>
 */
export const mapSuggestionIdToKey = (
    elementMapping: Record<string, Partial<TextContentElement>>,
    suggestionsDataMap: Record<string, SuggestionDataMap>
) => {
    const suggestionToKeyMap = new Map<string, string>()
    for (const key of Object.keys(elementMapping)) {
        const suggestionId = key.split('_')[1]
        if (suggestionId && suggestionsDataMap[suggestionId]) {
            suggestionToKeyMap.set(suggestionId, key)
        }
    }
    return suggestionToKeyMap
}

/**
 * Extracts content item element IDs from OPTIONGROUP's nested track changes.
 * @param change - The tracked change with OPTIONGROUP type
 * @param pendingIds - Set to add the extracted IDs to
 */
const extractOptionGroupIds = (
    change: TrackedChange,
    pendingIds: Set<string>
): void => {
    if (
        change.changeType === TrackChangeType.OPTIONGROUP &&
        change.trackChangeTypeToChangedTexts?.length
    ) {
        for (const nestedChange of change.trackChangeTypeToChangedTexts) {
            if (nestedChange.contentItemElementId) {
                pendingIds.add(nestedChange.contentItemElementId)
            }
        }
    }
}

/**
 * Extracts pending IDs from track changes data for bulk operations.
 * Only includes items that are not already approved or rejected.
 * @param trackChangesData - Complete track changes data from the store
 * @returns Array of content item element IDs that are pending processing
 */
const getPendingIdsFromTrackChanges = (
    trackChangesData: Record<string, TrackedChange>
): string[] => {
    const pendingIds = new Set<string>()

    for (const change of Object.values(trackChangesData)) {
        // Skip already resolved items
        if (
            change.status === TrackChangeStatus.APPROVED ||
            change.status === TrackChangeStatus.REJECTED
        ) {
            continue
        }

        // Add the contentItemElementId
        if (change.contentItemElementId) {
            pendingIds.add(change.contentItemElementId)
        }

        // Extract nested IDs from OPTIONGROUP changes
        extractOptionGroupIds(change, pendingIds)
    }

    return Array.from(pendingIds)
}

/**
 * Extracts IDs from filtered suggestion data.
 * @param suggestionsData - The filtered suggestion data map from the store
 * @returns Array of content item element IDs from suggestions
 */
const getIdsFromFilteredSuggestions = (
    suggestionsData: Record<string, SuggestionDataMap>
): string[] => {
    if (Object.keys(suggestionsData).length === 0) return []

    const result: string[] = []

    for (const suggestionMap of Object.values(suggestionsData)) {
        if (suggestionMap.suggestions?.length) {
            for (const suggestion of suggestionMap.suggestions) {
                const key = suggestion.data?.key
                if (key) {
                    result.push(key as string)
                }
            }
        }
    }

    return result
}

/**
 * Gets element IDs for bulk actions (Accept All/Reject All) based on the current filter.
 *
 * Behavior varies by filter:
 * - RESOLVED filter: Extracts pending IDs from trackChangesData (items needing resolution)
 * - OPEN/other filters: Uses filtered suggestions from suggestionsData
 *
 * Why different behavior for RESOLVED filter?
 * When users view the RESOLVED filter, they see already accepted/rejected changes.
 * However, "Accept All" and "Reject All" should operate on PENDING changes, not resolved ones.
 * The suggestionData is filtered to show only resolved items, so we must look at the
 * complete trackChangesData to find pending items that actually need processing.
 *
 * @param suggestionsData - The filtered suggestion data map from the store
 * @param trackChangesData - Complete track changes data from the store
 * @param suggestionCardFilter - Current filter type ('OPEN' or 'RESOLVED')
 * @returns Array of content item element IDs to be processed
 */
export const getElementIdsForBulkActions = (
    suggestionsData: Record<string, SuggestionDataMap>,
    trackChangesData?: Record<string, TrackedChange>,
    suggestionCardFilter?: string
): string[] => {
    // When RESOLVED filter is active, suggestionData contains only resolved items.
    // But bulk actions should operate on pending items, so we extract those from
    // the complete trackChangesData instead.
    const isResolvingPendingItems =
        suggestionCardFilter === TRACK_CHANGE_STATUS_VALUES.RESOLVED &&
        trackChangesData

    return isResolvingPendingItems
        ? getPendingIdsFromTrackChanges(trackChangesData)
        : getIdsFromFilteredSuggestions(suggestionsData)
}

/**
 * Determines if a text element should be considered for changes (such as editing, highlighting, or processing)
 * based on its changeType and the specified action.
 *
 * @param changeType - The type of change associated with the text element (e.g., 'added', 'deleted', 'modified').
 * @param action - The action being performed (e.g., 'accept', 'reject').
 * @returns boolean - True if the text element should be changed or processed according to the action; otherwise, false.
 */
const shouldModifyElement = (
    textElement: Partial<TextContentElement>,
    action: Action
): boolean =>
    (textElement.changeType?.toUpperCase() === TrackChangeType.ADDED &&
        (action === Action.accept || action === Action.acceptAll)) ||
    (textElement.changeType?.toUpperCase() === TrackChangeType.DELETED &&
        (action === Action.discard || action === Action.discardAll))

/**
 * Determines if a text element should be removed based on its changeType and the action.
 */
const shouldRemoveElement = (
    textElement: Partial<TextContentElement>,
    action: Action
): boolean =>
    (textElement.changeType?.toUpperCase() === TrackChangeType.ADDED &&
        (action === Action.discard || action === Action.discardAll)) ||
    (textElement.changeType?.toUpperCase() === TrackChangeType.DELETED &&
        (action === Action.accept || action === Action.acceptAll))

/**
 * Removes elements from content items by element id.
 */
const removeElementFromContentItems = (
    items: Partial<ContentItemViewNodesType>[],
    elementId: string
): Partial<ContentItemViewNodesType>[] =>
    items.filter((item) => !item.elements?.some((el) => el.id === elementId))

/**
 * Checks if a keyed element exists for a given text element in the content item view nodes.
 * A keyed element is identified by appending a specific suffix to the text element's ID.
 * @param contentItemViewNodes - The array of content item view nodes to search within.
 * @param textElement - The text content element to check for a corresponding keyed element.
 * @returns True if a keyed element exists; otherwise, false.
 */
const isKeyedElementExists = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    textElement: Partial<TextContentElement>
): boolean => {
    if (!textElement.id || !contentItemViewNodes.length) return false
    const keyElementId = `${textElement.id}_${KEY_ID_SUFFIX}`.toLowerCase()

    for (const item of contentItemViewNodes) {
        if (
            item.elements?.some((el) => el.id?.toLowerCase() === keyElementId)
        ) {
            return true
        }
    }

    return false
}

/**
 * Updates a partially keyed text element from the content item view nodes.
 * @param contentItemViewNodes - The array of content item view nodes to modify.
 * @param textElement - The text content element to update.
 * @param action - The action being performed (e.g., 'accept', 'reject').
 * @returns The updated array of content item view nodes.
 */
const updatePartialKeyedTextElement = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    textElement: Partial<TextContentElement>,
    action: Action
): Partial<ContentItemViewNodesType>[] => {
    if (!textElement.id) return contentItemViewNodes

    const isAccept = action === Action.accept || action === Action.acceptAll
    const isAdd =
        textElement.changeType?.toUpperCase() === TrackChangeType.ADDED
    const isDelete =
        textElement.changeType?.toUpperCase() === TrackChangeType.DELETED

    const shouldRemoveElement = (isAccept && isDelete) || (!isAccept && isAdd)
    const shouldModifyElement = (isAccept && isAdd) || (!isAccept && isDelete)

    return contentItemViewNodes.map((item) => {
        if (!item.elements) return item

        // Accept DELETE or Discard ADD: Remove the element
        if (shouldRemoveElement) {
            const filteredElements = item.elements.filter(
                (el) => el.id !== textElement.id
            )

            if (filteredElements.length !== item.elements.length) {
                return {
                    ...item,
                    elements: filteredElements,
                }
            }
        }
        // Accept ADD or Discard DELETE: Clear changeType, keep element
        else if (shouldModifyElement) {
            const updatedElements = item.elements.map((el) => {
                if (el.id === textElement.id) {
                    return {
                        ...el,
                        changeType: null,
                        authoredUserId: undefined,
                    }
                }
                return el
            })

            const hasChanges = !updatedElements.every(
                (el, i) => el === item?.elements?.[i]
            )
            if (hasChanges) {
                return {
                    ...item,
                    elements: updatedElements,
                }
            }
        }

        return item
    })
}

/**
 * Gets updated content item view nodes for non-sibling elements based on the action.
 * This function modifies the content item view nodes based on the action performed on non-sibling elements.
 * It handles the case where elements are added or deleted based on the action type.
 * @param contentItemViewNodes - The current content item view nodes.
 * @param nonSiblingElements - single element with no siblings, it indicates a full addition or deletion. Otherwise, it means the element was partially updated.
 * @param action - The action to perform (accept, discard, etc.).
 * @return The updated content item view nodes after processing the non-sibling elements.
 *  */
export const getUpdatedContentItemViewNodesForNonSiblingsElements = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    nonSiblingElements: Record<string, Partial<TextContentElement>>,
    action: Action
): Partial<ContentItemViewNodesType>[] => {
    let updatedContentItems = [...contentItemViewNodes]

    Object.values(nonSiblingElements).forEach((textElement) => {
        if (isKeyedElementExists(updatedContentItems, textElement)) {
            updatedContentItems = updatePartialKeyedTextElement(
                updatedContentItems,
                textElement,
                action
            )
        } else if (shouldModifyElement(textElement, action)) {
            updatedContentItems = ModifyTextElementById(
                textElement.id as string,
                updatedContentItems
            )
        } else if (shouldRemoveElement(textElement, action)) {
            updatedContentItems = removeElementFromContentItems(
                updatedContentItems,
                textElement.id as string
            )
        }
    })

    return updatedContentItems
}

/**
 * Processes a product fetch response after a track change accept/discard action and
 * returns the resulting content item view nodes. For bulk actions, non-sibling element
 * updates are applied on top of the fetched content. The caller is responsible for
 * updating the tree with the returned nodes.
 *
 * @param currentContext - The current track changes context
 * @param response - The response from the product fetcher
 * @param onContentViewFetchSuccess - Function to merge the fetch response into content nodes
 */
export const buildContentItemsFromFetchResponse = async (
    currentContext: UpdatedTrackChangesData | undefined,
    response:
        | GetProjectProductFamilyViewResponse
        | GetProjectNuggetContentViewResponse,
    onContentViewFetchSuccess: (
        response:
            | GetProjectProductFamilyViewResponse
            | GetProjectNuggetContentViewResponse,
        args: [],
        flag: boolean
    ) => Promise<Partial<ContentItemViewNodesType>[] | undefined>
): Promise<Partial<ContentItemViewNodesType>[] | undefined> => {
    if (!currentContext) return

    const { action, nonSiblingElements } = currentContext
    const isBulk = action === Action.acceptAll || action === Action.discardAll

    const optimisticTreeViewNodes = await onContentViewFetchSuccess(
        response,
        [],
        true
    )

    if (optimisticTreeViewNodes) {
        const latestContentItemViewNodes = [
            ...(isBulk
                ? getUpdatedContentItemViewNodesForNonSiblingsElements(
                      optimisticTreeViewNodes,
                      nonSiblingElements || {},
                      action
                  )
                : optimisticTreeViewNodes),
        ]

        return latestContentItemViewNodes
    }
}
